import React, { useRef, useEffect, useState, useContext } from "react";
import mapboxgl from "mapbox-gl";
import { AppContext } from "../context/AppContext";
import { BounceLoader } from "react-spinners"; // Optional spinner
import dedupeLocations from "../utils/dedupeLocations";
import filterLocations from "../utils/filterLocations";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_KEY;

const tnftIcon = "/assets/tnft_logo.png";

// The API only answers requests from map.thenextfunthing.com, so local dev can
// point this at a saved copy instead (see .env.development.local)
const LOCATIONS_URL =
  process.env.REACT_APP_LOCATIONS_URL ||
  "https://82sdqbf61k.execute-api.us-west-1.amazonaws.com/locations";

// Opening view: the part of LA where most venues are. Outliers (Santa Barbara,
// Palm Springs, San Diego) are still on the map, just not in the first frame.
const LA_BOUNDS = [
  [-118.52, 33.86],
  [-118.12, 34.18],
];

const BRAND_YELLOW = "#f2ce1b";
const INK = "#000000";

const emptyCollection = { type: "FeatureCollection", features: [] };

// On phones the results sheet covers the bottom of the map, so leave room
// for it when framing results
const fitPadding = () =>
  window.matchMedia("(max-width: 899px)").matches
    ? { top: 50, left: 40, right: 40, bottom: window.innerHeight * 0.4 + 16 }
    : 80;

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Mapbox serializes array properties to JSON strings on features it returns
// from events, so accept either form
const parseGuides = (guides) =>
  typeof guides === "string" ? JSON.parse(guides) : guides || [];

const popupHtml = (properties) => {
  const guides = parseGuides(properties.guides)
    .map(
      (guide) =>
        `<li><a href="https://thenextfunthing.com/la-guide/${encodeURIComponent(
          guide.slug
        )}" target="_blank" rel="noopener noreferrer">${escapeHtml(
          guide.Name
        )}</a></li>`
    )
    .join("");

  return `
    <div class="venue-popup">
      <h3>${escapeHtml(properties.Locations)}</h3>
      <p>${escapeHtml(properties.Address)}</p>
      <p class="venue-popup__label">Featured in:</p>
      <ul>${guides}</ul>
    </div>
  `;
};

function Map() {
  const {
    address1,
    address2,
    middleGround,
    filters,
    locations,
    setLocations,
    selectedId,
    setSelectedId,
  } = useContext(AppContext);
  const mapContainer = useRef(null);
  const map = useRef(null);
  const popup = useRef(null);
  const addressMarkers = useRef({});
  const [mapReady, setMapReady] = useState(false);
  const [loading, setLoading] = useState(true); // Loading state

  // Create the map exactly once
  useEffect(() => {
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v12",
      bounds: LA_BOUNDS,
      fitBoundsOptions: { padding: 20 },
    });
    map.current.addControl(
      new mapboxgl.NavigationControl({ showCompass: false }),
      "top-right"
    );

    map.current.on("load", () => {
      map.current.loadImage(tnftIcon, (error, image) => {
        if (error) return;
        if (!map.current.hasImage("custom-icon")) {
          map.current.addImage("custom-icon", image);
        }
        addLayers();
        loadGeoJsonData();
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addLayers = () => {
    const m = map.current;

    m.addSource("venues", {
      type: "geojson",
      data: emptyCollection,
      cluster: true,
      clusterMaxZoom: 13,
      clusterRadius: 45,
    });

    m.addLayer({
      id: "clusters",
      type: "circle",
      source: "venues",
      filter: ["has", "point_count"],
      paint: {
        "circle-color": BRAND_YELLOW,
        "circle-stroke-color": INK,
        "circle-stroke-width": 1.5,
        // Bigger clusters get bigger circles, within a narrow range
        "circle-radius": [
          "step",
          ["get", "point_count"],
          14,
          10,
          18,
          30,
          23,
          80,
          28,
        ],
      },
    });

    m.addLayer({
      id: "cluster-count",
      type: "symbol",
      source: "venues",
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["get", "point_count_abbreviated"],
        "text-font": ["DIN Pro Medium", "Arial Unicode MS Bold"],
        "text-size": 13,
        "text-allow-overlap": true,
      },
      paint: { "text-color": INK },
    });

    m.addLayer({
      id: "points",
      type: "symbol",
      source: "venues",
      filter: ["!", ["has", "point_count"]],
      layout: {
        "icon-image": "custom-icon",
        "icon-size": ["interpolate", ["linear"], ["zoom"], 10, 0.1, 16, 0.16],
        "icon-allow-overlap": true,
      },
    });

    // Middle ground results get their own unclustered source so every result
    // is always visible
    m.addSource("results", { type: "geojson", data: emptyCollection });
    m.addLayer({
      id: "result-points",
      type: "symbol",
      source: "results",
      layout: {
        "icon-image": "custom-icon",
        "icon-size": 0.16,
        "icon-allow-overlap": true,
      },
    });

    // Numbered badge on each result so it matches the list
    m.addLayer({
      id: "result-badges",
      type: "circle",
      source: "results",
      paint: {
        "circle-radius": 9,
        "circle-color": INK,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 1.5,
        "circle-translate": [15, -15],
      },
    });
    m.addLayer({
      id: "result-ranks",
      type: "symbol",
      source: "results",
      layout: {
        "text-field": ["to-string", ["get", "rank"]],
        "text-font": ["DIN Pro Bold", "Arial Unicode MS Bold"],
        "text-size": 11,
        "text-allow-overlap": true,
        "text-ignore-placement": true,
      },
      paint: {
        "text-color": "#ffffff",
        "text-translate": [15, -15],
      },
    });

    m.on("click", "clusters", (e) => {
      const feature = e.features[0];
      m.getSource("venues").getClusterExpansionZoom(
        feature.properties.cluster_id,
        (err, zoom) => {
          if (err) return;
          m.easeTo({ center: feature.geometry.coordinates, zoom });
        }
      );
    });

    const showPopup = (e) => {
      const feature = e.features[0];
      const coordinates = feature.geometry.coordinates.slice();

      // Ensure the popup is shown at the correct position (prevent map shift)
      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }

      popup.current?.remove();
      popup.current = new mapboxgl.Popup({ offset: 15, maxWidth: "280px" })
        .setLngLat(coordinates)
        .setHTML(popupHtml(feature.properties))
        .addTo(m);
    };
    m.on("click", "points", showPopup);
    // Results open through the selection, so the list highlights them too
    m.on("click", "result-points", (e) =>
      setSelectedId(e.features[0].properties.id)
    );

    // Change the cursor to pointer when hovering over anything clickable
    ["clusters", "points", "result-points"].forEach((layer) => {
      m.on("mouseenter", layer, () => {
        m.getCanvas().style.cursor = "pointer";
      });
      m.on("mouseleave", layer, () => {
        m.getCanvas().style.cursor = "";
      });
    });

    setMapReady(true);
  };

  const loadGeoJsonData = () => {
    fetch(LOCATIONS_URL)
      .then((response) => response.json())
      .then((data) => {
        setLocations(dedupeLocations(data.data.features));
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error loading GeoJSON:", error);
        setLoading(false);
      });
  };

  // Browsing: show all venues (or the filtered ones), clustered
  useEffect(() => {
    if (!mapReady) return;
    const features =
      filters.length > 0 ? filterLocations(locations, filters) : locations;
    map.current.getSource("venues").setData({
      type: "FeatureCollection",
      features,
    });
  }, [mapReady, locations, filters]);

  // Searching: hide the browse layers, show only the results and zoom to them
  useEffect(() => {
    if (!mapReady) return;
    const m = map.current;
    const searching = middleGround.length > 0;
    const browseVisibility = searching ? "none" : "visible";

    ["clusters", "cluster-count", "points"].forEach((layer) =>
      m.setLayoutProperty(layer, "visibility", browseVisibility)
    );
    m.getSource("results").setData({
      type: "FeatureCollection",
      features: middleGround,
    });
    popup.current?.remove();
    setSelectedId(null);

    if (searching) {
      // Frame both addresses as well as the results between them
      const bounds = new mapboxgl.LngLatBounds();
      middleGround.forEach((location) =>
        bounds.extend(location.geometry.coordinates)
      );
      [address1, address2].forEach(
        (address) => address && bounds.extend(address.center)
      );
      m.fitBounds(bounds, { padding: fitPadding(), maxZoom: 15 });
    } else {
      m.fitBounds(LA_BOUNDS, { padding: 20 });
    }
    // Only reframe when the results change, not on every address pick
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, middleGround]);

  // Open the selected result's popup, whether it was picked in the list or
  // on the map
  useEffect(() => {
    if (!mapReady || !selectedId) return;
    const m = map.current;
    const location = middleGround.find(
      (result) => result.properties.id === selectedId
    );
    if (!location) return;

    m.setLayoutProperty("result-points", "icon-size", [
      "case",
      ["==", ["get", "id"], selectedId],
      0.22,
      0.16,
    ]);

    popup.current?.remove();
    popup.current = new mapboxgl.Popup({ offset: 20, maxWidth: "280px" })
      .setLngLat(location.geometry.coordinates)
      .setHTML(popupHtml(location.properties))
      .addTo(m);
    popup.current.on("close", () => {
      m.setLayoutProperty("result-points", "icon-size", 0.16);
      setSelectedId((current) => (current === selectedId ? null : current));
    });

    // Zoom in far enough that results sitting close together separate
    m.easeTo({
      center: location.geometry.coordinates,
      zoom: Math.max(m.getZoom(), 14),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, selectedId]);

  // Drop a red pin on each address as soon as it's picked
  useEffect(() => {
    if (!mapReady) return;
    const m = map.current;

    Object.entries({ address1, address2 }).forEach(([key, address]) => {
      const existing = addressMarkers.current[key];
      if (existing?.address === address) return;

      existing?.marker.remove();
      delete addressMarkers.current[key];
      if (!address) return;

      const marker = new mapboxgl.Marker({ color: "#e0262c" })
        .setLngLat(address.center)
        .setPopup(
          new mapboxgl.Popup({ offset: 35 }).setText(address.place_name)
        )
        .addTo(m);
      addressMarkers.current[key] = { marker, address };

      // Bring a newly picked address into view if it's off screen
      if (!m.getBounds().contains(address.center)) {
        m.easeTo({ center: address.center });
      }
    });
  }, [mapReady, address1, address2]);

  return (
    <div>
      {loading && ( // Show spinner while loading
        <div className="spinner-container">
          <BounceLoader color={BRAND_YELLOW} loading={loading} size={60} />
        </div>
      )}
      <div ref={mapContainer} className="map-container" />
    </div>
  );
}

export default Map;
